// src/components/canvass/AddContactModal.tsx
// Modal for adding a new contact/address during field canvassing
import React from 'react';
import {
  X, User, Phone, Mail, Home, MapPin, Plus, Loader2
} from 'lucide-react';

export interface NewContactData {
  name: string;
  phone: string;
  email: string;
  notes: string;
  street: string;
  unit: string;
  city: string;
  state: string;
  zipCode: string;
  lat: number;
  lng: number;
  useMapPicker: boolean;
}

export interface AddContactModalProps {
  newContact: NewContactData;
  onContactChange: (updates: Partial<NewContactData>) => void;
  onSubmit: () => void;
  onClose: () => void;
  isSubmitting: boolean;
  mapPickerActive: boolean;
  onToggleMapPicker: () => void;
  userLocation: { lat: number; lng: number } | null;
}

const AddContactModal: React.FC<AddContactModalProps> = ({
  newContact,
  onContactChange,
  onSubmit,
  onClose,
  isSubmitting,
  mapPickerActive,
  onToggleMapPicker,
  userLocation,
}) => {
  const handleToggleMapPicker = () => {
    if (mapPickerActive) {
      onToggleMapPicker();
    } else {
      onToggleMapPicker();
      // Use current location as starting point
      if (userLocation && !newContact.lat) {
        onContactChange({
          lat: userLocation.lat,
          lng: userLocation.lng,
        });
      }
    }
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-end justify-center z-50 md:items-center">
      <div className="bg-white w-full max-w-lg rounded-t-2xl md:rounded-2xl shadow-xl max-h-[90vh] overflow-hidden flex flex-col">
        <div className="p-4 border-b flex items-center justify-between flex-shrink-0">
          <div>
            <h2 className="font-semibold text-gray-900">Add New Contact</h2>
            <p className="text-sm text-gray-500">Add a stop that wasn't on your list</p>
          </div>
          <button onClick={onClose} className="p-2">
            <X className="h-5 w-5 text-gray-400" />
          </button>
        </div>

        <div className="p-4 space-y-4 overflow-y-auto flex-1">
          {/* Contact Info */}
          <div>
            <label className="block text-xs font-medium text-gray-500 uppercase tracking-wide mb-2">
              Contact Information
            </label>
            <div className="space-y-3">
              <div className="relative">
                <User className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
                <input
                  type="text"
                  placeholder="Contact Name"
                  value={newContact.name}
                  onChange={(e) => onContactChange({ name: e.target.value })}
                  className="w-full pl-10 pr-4 py-2.5 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                />
              </div>
              <div className="relative">
                <Phone className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
                <input
                  type="tel"
                  placeholder="Phone Number"
                  value={newContact.phone}
                  onChange={(e) => onContactChange({ phone: e.target.value })}
                  className="w-full pl-10 pr-4 py-2.5 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                />
              </div>
              <div className="relative">
                <Mail className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
                <input
                  type="email"
                  placeholder="Email Address"
                  value={newContact.email}
                  onChange={(e) => onContactChange({ email: e.target.value })}
                  className="w-full pl-10 pr-4 py-2.5 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                />
              </div>
            </div>
          </div>

          {/* Address */}
          <div>
            <label className="block text-xs font-medium text-gray-500 uppercase tracking-wide mb-2">
              Address <span className="text-red-500">*</span>
            </label>
            <div className="space-y-3">
              <div className="relative">
                <Home className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
                <input
                  type="text"
                  placeholder="Street Address"
                  value={newContact.street}
                  onChange={(e) => onContactChange({ street: e.target.value })}
                  className="w-full pl-10 pr-4 py-2.5 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                  required
                />
              </div>
              <input
                type="text"
                placeholder="Apt/Unit (optional)"
                value={newContact.unit}
                onChange={(e) => onContactChange({ unit: e.target.value })}
                className="w-full px-4 py-2.5 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
              />
              <div className="grid grid-cols-3 gap-2">
                <input
                  type="text"
                  placeholder="City"
                  value={newContact.city}
                  onChange={(e) => onContactChange({ city: e.target.value })}
                  className="w-full px-3 py-2.5 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                  required
                />
                <input
                  type="text"
                  placeholder="State"
                  value={newContact.state}
                  onChange={(e) => onContactChange({ state: e.target.value.toUpperCase().slice(0, 2) })}
                  maxLength={2}
                  className="w-full px-3 py-2.5 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                  required
                />
                <input
                  type="text"
                  placeholder="ZIP"
                  value={newContact.zipCode}
                  onChange={(e) => onContactChange({ zipCode: e.target.value })}
                  className="w-full px-3 py-2.5 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                />
              </div>
            </div>
          </div>

          {/* Location Picker */}
          <div>
            <div className="flex items-center justify-between mb-2">
              <label className="block text-xs font-medium text-gray-500 uppercase tracking-wide">
                Pin Location on Map
              </label>
              <button
                type="button"
                onClick={handleToggleMapPicker}
                className={`text-xs px-3 py-1 rounded-full ${
                  mapPickerActive
                    ? 'bg-indigo-100 text-indigo-700'
                    : 'bg-gray-100 text-gray-600'
                }`}
              >
                {mapPickerActive ? 'Picking...' : 'Pick Location'}
              </button>
            </div>
            {newContact.lat !== 0 && (
              <p className="text-xs text-green-600 flex items-center">
                <MapPin className="h-3 w-3 mr-1" />
                Location set: {newContact.lat.toFixed(5)}, {newContact.lng.toFixed(5)}
              </p>
            )}
            {mapPickerActive && (
              <p className="text-xs text-indigo-600 mt-1">
                Tap on the map to set the address location
              </p>
            )}
          </div>

          {/* Notes */}
          <div>
            <label className="block text-xs font-medium text-gray-500 uppercase tracking-wide mb-2">
              Notes
            </label>
            <textarea
              placeholder="Any additional notes about this contact..."
              value={newContact.notes}
              onChange={(e) => onContactChange({ notes: e.target.value })}
              rows={2}
              className="w-full px-4 py-2.5 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-indigo-500 focus:border-transparent resize-none"
            />
          </div>
        </div>

        <div className="p-4 border-t bg-gray-50 flex-shrink-0">
          <p className="text-xs text-gray-500 text-center mb-3">
            Your current GPS location will be recorded for accountability
          </p>
          <button
            onClick={onSubmit}
            disabled={!newContact.street || !newContact.city || !newContact.state || isSubmitting}
            className="w-full py-3 bg-indigo-600 text-white rounded-lg font-medium hover:bg-indigo-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center"
          >
            {isSubmitting ? (
              <>
                <Loader2 className="h-5 w-5 mr-2 animate-spin" />
                Adding...
              </>
            ) : (
              <>
                <Plus className="h-5 w-5 mr-2" />
                Add Contact
              </>
            )}
          </button>
        </div>
      </div>
    </div>
  );
};

export default AddContactModal;
